/**
 * CheckoutPage — pricing table + redirect to Stripe Checkout.
 *
 * Renders two plans (PAYG monthly / Annual) from the backend's /billing/plans
 * endpoint. User picks one → we POST /billing/checkout → Stripe returns a URL →
 * we redirect the browser. Stripe's hosted flow handles card entry, 3DS, and
 * returns to our /billing/success page on completion.
 *
 * Trial users see the signup bonus prominently so they understand the upfront
 * value. Existing subscribers see their current plan badged.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import billingApi from '../../services/billingApi';
import { useBilling } from '../../context/BillingContext';

function formatPrice(usd) {
  return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function PlanCard({ plan, currentPlanType, onSelect, busy }) {
  const isCurrent = plan.id === currentPlanType;
  const isAnnual = plan.interval === 'year';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderWidth: isAnnual ? 2 : 1,
        borderColor: isAnnual ? 'primary.main' : 'divider',
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {plan.name}
          </Typography>
          {isCurrent && (
            <CheckCircleIcon color="success" fontSize="small" titleAccess="Current plan" />
          )}
        </Stack>
        <Typography variant="h3" fontWeight={700} sx={{ mt: 2 }}>
          {formatPrice(plan.price_usd)}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            /{plan.interval}
          </Typography>
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {plan.description}
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 2 }}>
          <Typography variant="body2">
            ✓ {plan.included_credits_per_seat} credits/seat/month
          </Typography>
          <Typography variant="body2">
            ✓ Overage at ${plan.overage_price_usd.toFixed(2)}/credit
          </Typography>
          {isAnnual && <Typography variant="body2">✓ Save 20% vs monthly</Typography>}
        </Stack>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          fullWidth
          variant={isAnnual ? 'contained' : 'outlined'}
          disabled={busy || isCurrent}
          onClick={() => onSelect(plan.id)}
        >
          {isCurrent ? 'Current plan' : isAnnual ? 'Switch to annual' : 'Start PAYG'}
        </Button>
      </CardActions>
    </Card>
  );
}

export default function CheckoutPage({ onCancel }) {
  const { planType, billingEnabled } = useBilling();
  const [plans, setPlans] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await billingApi.getPlans();
        if (!cancelled) setPlans(res.data);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.detail || err?.message || 'Failed to load plans');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = useCallback(async (planId) => {
    setBusy(true);
    setSubmitError(null);
    try {
      const origin = window.location.origin;
      const res = await billingApi.createCheckoutSession({
        plan: planId,
        seat_count: 1,
        successUrl: `${origin}/billing/success`,
        cancelUrl: `${origin}/billing/cancel`,
      });
      if (res.data?.url) {
        window.location.href = res.data.url; // Full redirect — not an SPA nav.
      } else {
        setSubmitError('Checkout provider did not return a redirect URL');
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || err?.message || 'Checkout failed');
    } finally {
      setBusy(false);
    }
  }, []);

  if (!billingEnabled) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">
          Billing is not enabled on this environment. You have full access while on the pilot.
        </Alert>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{String(loadError)}</Alert>
      </Box>
    );
  }

  if (!plans) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Choose your plan
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Flexible monthly billing with no commitment — or save 20% with an annual plan.
        New accounts get {plans.signup_bonus_credits} free credits to explore.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(submitError)}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <PlanCard
            plan={plans.seat_monthly}
            currentPlanType={planType}
            onSelect={handleSelect}
            busy={busy}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PlanCard
            plan={plans.seat_annual}
            currentPlanType={planType}
            onSelect={handleSelect}
            busy={busy}
          />
        </Grid>
      </Grid>

      {onCancel && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
          <Button variant="text" onClick={onCancel} disabled={busy}>
            Not now
          </Button>
        </Stack>
      )}
    </Box>
  );
}
